import { Injectable } from '@angular/core';
import { AlertController } from 'ionic-angular';
import { ActionSheetController } from 'ionic-angular'

import { TranslateService } from '@ngx-translate/core';
import { Keyboard } from 'ionic-angular/platform/keyboard';

@Injectable()
export class AlertProvider {

  constructor(private translate: TranslateService, 
    private alertCtrl: AlertController, 
    private actionSheetCtrl: ActionSheetController,
    private keyboard: Keyboard) {

  }

  /**
  * Displays an actionsheet for changing the display language.
  */
  public languageActionSheet(handler: (lang: string) => void){
    let actionSheet = this.actionSheetCtrl.create({
     title: this.translate.instant('ACCOUNT.CHANGE_LANGUAGE'),
     buttons: [
       {
         text: this.translate.instant('LANGUAGE.EN'),
         handler: () => {
           handler("en");
         }
       },
       {
         text: this.translate.instant('LANGUAGE.NL'),
         handler: () => {
           handler("nl");
         }
       },
       {
         text: this.translate.instant('GENERIC.CANCEL'),
         role: 'cancel'
       }
     ]
   });

   actionSheet.present();
  }

  /**
  * Creates an error dialog showing the given error message.
  */
  public error(errorMessage: string) {
    let alert = this.alertCtrl.create({
      title: this.translate.instant('GENERIC.ERROR'),
      subTitle: errorMessage,
      buttons: [this.translate.instant('GENERIC.DISSMISS')]
    });
    alert.present();
  }

  /**
  * Creates a dialog showing the given title and message.
  */
  public simple(title: string, message: string) {
    let alert = this.alertCtrl.create({
      title: this.translate.instant(title),
      subTitle: this.translate.instant(message),
      buttons: [this.translate.instant('GENERIC.DISSMISS')]
    });
    alert.present();
  }

  /**
  * Creates a dialog showing the given title, message and one button.
  */
  public customButton(title: string, message: string, button: string) {
    let alert = this.alertCtrl.create({
      title: this.translate.instant(title),
      subTitle: this.translate.instant(message),
      buttons: [this.translate.instant(button)]
    });
    alert.present();
  }

  /**
  * Creates a dialog for making sure the user wants to go back from the order page.
  */
  public cancelOrderPageAlert(handler: () => void) {
    let alert = this.alertCtrl.create({
      title: this.translate.instant('ORDER.BACK_DIALOG_TITLE'),
      subTitle: this.translate.instant('ORDER.BACK_DIALOG_MESSAGE'),
      buttons: [
      {
        text: this.translate.instant('GENERIC.YES'),
        handler: handler
      },
      {
          text: this.translate.instant('GENERIC.NO')
      }]
    });
    alert.present();
  }

  /**
  * Creates a dialog for entering a specific product quantity.
  */
  public showPrompt(productName: string, currentValue: string, handler: (data: any) => void) {
    let prompt = this.alertCtrl.create({
      title: productName,
      message: this.translate.instant('BASKET.CUSTOM_QUANTITY_MESSAGE'),
      inputs: [
        {
          name: 'quantity',
          placeholder: this.translate.instant('BASKET.QUANTITY'),
          value: currentValue,
          type: 'number'
        },
      ],
      buttons: [
        {
          text: this.translate.instant('GENERIC.CANCEL')
        },
        {
          text: this.translate.instant('GENERIC.DONE'),
          handler: handler
        }
      ]
    });

    prompt.present().then(() => {  
      const firstInput: any = document.querySelector('ion-alert input');  
      firstInput.focus();  
      return;
    });
  }

  /**
  * Creates a dialog for entering customer name.
  */
  public showCustomerNamePrompt(firstName: string, lastName: string, handler: (data: any) => void) {
    let prompt = this.alertCtrl.create({
      title: this.translate.instant('ACCOUNT.EDIT_NAME_TITLE'),
      message: this.translate.instant('ACCOUNT.EDIT_NAME_MESSAGE'),
      inputs: [
        {
          name: 'first',
          placeholder: this.translate.instant('REGISTER.FIRST_NAME'),
          value: firstName,
          type: 'text'
        },
        {
          name: 'last',
          placeholder: this.translate.instant('REGISTER.LAST_NAME'),
          value: lastName,
          type: 'text'
        }
      ],
      buttons: [
        {
          text: this.translate.instant('GENERIC.CANCEL')
        },
        {
          text: this.translate.instant('GENERIC.DONE'),
          handler: handler
        }
      ]
    });
    prompt.present();
  }

  /**
  * Creates a dialog for entering customer phone.
  */
  public showCustomerPhonePrompt(phone: string, handler: (data: any) => void) {
    let prompt = this.alertCtrl.create({
      title: this.translate.instant('ACCOUNT.EDIT_PHONE_TITLE'),
      message: this.translate.instant('ACCOUNT.EDIT_PHONE_MESSAGE'),
      inputs: [
        {
          name: 'phone',
          placeholder: this.translate.instant('REGISTER.PHONE'),
          value: phone,
          type: 'tel'
        }
      ],
      buttons: [
        {
          text: this.translate.instant('GENERIC.CANCEL')
        },
        {
          text: this.translate.instant('GENERIC.DONE'),
          handler: handler
        }
      ]
    });
    prompt.present();
  }

  /**
  * Creates a dialog for entering customer mail.
  */
  public showCustomerMailPrompt(mail: string, handler: (data: any) => void) {
    let prompt = this.alertCtrl.create({
      title: this.translate.instant('ACCOUNT.EDIT_MAIL_TITLE'),
      message: this.translate.instant('ACCOUNT.EDIT_MAIL_MESSAGE'),
      inputs: [
        {
          name: 'mail',
          placeholder: this.translate.instant('REGISTER.MAIL'),
          value: mail,
          type: 'text'
        }
      ],
      buttons: [
        {
          text: this.translate.instant('GENERIC.CANCEL')
        },
        {
          text: this.translate.instant('GENERIC.DONE'),
          handler: handler
        }
      ]
    });
    prompt.present();
  }

  /**
  * Creates a dialog for entering customer address details.
  */
  public showCustomerAddressPrompt(address: string, housenumber: string, city: string, zipcode: string, country: string, handler: (data: any) => void) {
    let prompt = this.alertCtrl.create({
      title: this.translate.instant('ACCOUNT.EDIT_ADDRESS_TITLE'),
      message: this.translate.instant('ACCOUNT.EDIT_ADDRESS_MESSAGE'),
      inputs: [
        {
          name: 'address',
          placeholder: this.translate.instant('REGISTER.ADDRESS'),
          value: address,
          type: 'text'
        },
        {
          name: 'housenumber',
          placeholder: this.translate.instant('REGISTER.HOUSE_NUMBER'),
          value: housenumber,
          type: 'text'
        },
        {
          name: 'city',
          placeholder: this.translate.instant('REGISTER.CITY'),
          value: city,
          type: 'text'
        },
        {
          name: 'zipcode',
          placeholder: this.translate.instant('REGISTER.ZIPCODE'),
          value: zipcode,
          type: 'text'
        },
        {
          name: 'country',
          placeholder: this.translate.instant('REGISTER.COUNTRY'),
          value: country,
          type: 'text'
        }
      ],
      buttons: [
        {
          text: this.translate.instant('GENERIC.CANCEL')
        },
        {
          text: this.translate.instant('GENERIC.DONE'),
          handler: handler
        }
      ]
    });
    prompt.present();
  }
}


// WEBPACK FOOTER //
// ./src/providers/alert/alert.ts